import { useTranslation } from "react-i18next";
import { Boxes, Plus, SearchX } from "lucide-react";
import type { Package } from "@nh/shared";
import { Alert, Button } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import { PackagesTable } from "./PackagesTable";
import { useApiErrorMessage } from "../useApiErrorMessage";

export interface PackagesListBodyProps {
  packages: readonly Package[];
  loading: boolean;
  error: Error | null;
  /** `true` si hay filtros puestos: cambia el estado de vacío. */
  filtered: boolean;
  onRetry: () => void;
  onCreate: () => void;
  onResetFilters: () => void;
  onEdit: (pkg: Package) => void;
  onArchive: (pkg: Package) => void;
  archivingId?: string | null;
}

/**
 * El cuerpo del listado: esqueleto, error, vacío o tabla.
 *
 * Los cuatro estados están AQUÍ y no repartidos por la página porque son
 * excluyentes entre sí, y cuando se escriben sueltos siempre acaba
 * habiendo una combinación que pinta dos a la vez —el esqueleto encima
 * de un error, típicamente—.
 *
 * "Vacío" son dos estados distintos, y confundirlos es el clásico:
 * "todavía no hay paquetes" invita a crear el primero; "ningún paquete
 * coincide" invita a limpiar el filtro. Enseñar el primero cuando pasa
 * el segundo hace pensar que el catálogo se borró.
 */
export function PackagesListBody({
  packages,
  loading,
  error,
  filtered,
  onRetry,
  onCreate,
  onResetFilters,
  onEdit,
  onArchive,
  archivingId = null,
}: PackagesListBodyProps) {
  const { t } = useTranslation();
  const errorMessage = useApiErrorMessage(error);

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="error" title={t("No se pudieron cargar los paquetes")}>
          <p>{errorMessage}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
            {t("Reintentar")}
          </Button>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-5" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t("Cargando los paquetes…")}</span>
        <SkeletonTable rows={5} columns={7} />
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="p-4">
        {filtered ? (
          <EmptyState
            icon={<SearchX className="h-5 w-5 text-ds-ink-3" />}
            title={t("Sin resultados")}
            description={t("Ningún paquete coincide con esos filtros.")}
            action={
              <Button variant="secondary" size="sm" onClick={onResetFilters}>
                {t("Limpiar filtros")}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Boxes className="h-5 w-5 text-ds-ink-3" />}
            title={t("Todavía no hay paquetes")}
            description={t(
              "Un paquete junta varios productos y se cobra cada mes con descuento.",
            )}
            action={
              <Button size="sm" leftIcon={<Plus />} onClick={onCreate}>
                {t("Nuevo paquete")}
              </Button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <PackagesTable
      packages={packages}
      onEdit={onEdit}
      onArchive={onArchive}
      archivingId={archivingId}
    />
  );
}
